import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GetRowIdParams, GridOptions, ValueFormatterParams } from 'ag-grid-community';

import { DeviceClaimProgress, MacSerialNumberDevice } from '@shure/cloud/shared/models/devices';
import { BaseTableComponent } from '@shure/cloud/shared/ui/components';
import {
	AgGridResponsiveWrapperComponent,
	ProgressRendererComponent,
	ProgressStatus
} from '@shure/shared/angular/ui/components/ag-grid-next';

import {
	ClaimStatusCellData,
	claimStatusCellDataComparator,
	claimStatusKeyCreator
} from '../../../../../models/claim-status-cell-data';
import {
	MacSerialConfirmationTableRowData,
	mapMacSerialClaimDataToMacSerialConfirmationTableRowData
} from '../../../../../models/mac-serial-confirmation-row-data';
import { mapProgressStatusToTranslocoKey } from '../../../../../models/mappers/map-progress-status-to-transloco-key';

interface MacSerialConfirmationTableContext {
	componentParent: MacSerialConfirmationTableComponent;
}

@Component({
	selector: 'sh-mac-serial-confirmation-table',
	imports: [AgGridAngular, AgGridResponsiveWrapperComponent],
	templateUrl: './mac-serial-confirmation-table.component.html',
	styleUrls: ['./mac-serial-confirmation-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class MacSerialConfirmationTableComponent extends BaseTableComponent<MacSerialConfirmationTableRowData> {
	@Input()
	public set devices(_devices: DeviceClaimProgress<MacSerialNumberDevice>['devices'][0][] | null) {
		this.gridData = mapMacSerialClaimDataToMacSerialConfirmationTableRowData(_devices ?? []);
	}

	// AG Grid Members
	public gridData: MacSerialConfirmationTableRowData[] = [];

	public context: MacSerialConfirmationTableContext = {
		componentParent: this
	};

	public readonly popupParent: HTMLElement | null = document.querySelector('body');

	public readonly columnDefs: ColDef<MacSerialConfirmationTableRowData>[] = [
		//
		// MAC Address
		//
		this.createMacAddressColumn({
			field: 'macAddress',
			lockVisible: true
		}),
		//
		// Serial Number
		//
		this.createSerialNumberColumn({
			field: 'serialNumber',
			lockVisible: true
		}),
		//
		// Status
		//
		this.createCustomRendererColumn({
			field: 'claimStatusCellData',
			headerName: this.translocoService.translate('cloud.add-devices-dialog.status'),
			cellRenderer: ProgressRendererComponent,
			cellClass: 'sh-claim-status-cell-renderer',
			filter: 'agSetColumnFilter',
			filterParams: {
				comparator: claimStatusCellDataComparator,
				valueFormatter: (params: ValueFormatterParams<unknown, ClaimStatusCellData>) =>
					this.translocoService.translate(
						mapProgressStatusToTranslocoKey(params.value?.status ?? ProgressStatus.InProgress)
					)
			},
			keyCreator: claimStatusKeyCreator,
			comparator: claimStatusCellDataComparator,
			lockVisible: true
		})
	];

	constructor(private readonly translocoService: TranslocoService) {
		super(translocoService);
		this.context.componentParent = this;
	}

	public getRowId(params: GetRowIdParams<MacSerialConfirmationTableRowData>): string {
		return params.data.id;
	}

	protected override getGridOptions(): GridOptions<MacSerialConfirmationTableRowData> {
		return {
			...super.getGridOptions(),
			theme: 'legacy',
			// Attaching the AG Grid popups to the document body currently does not work with Prism dialogs.
			// For now we'll attach to the grid and take the hit on menus getting cut off on smaller tables.
			// TODO: remove this once Prism dialogs have better AG Grid support
			popupParent: undefined
		};
	}
}
